// ============================================================================
// ui/project-tray.js — the staging tray: plates primed in the EDITOR, sent later
// ============================================================================
// CUSTOM_WEIGHTDESIGNER_BY_CLAUDE (Ryan 2026-07-31)
//
// ── WHAT WENT WRONG, IN RYAN'S WORDS ────────────────────────────────────────────────
// The WD→Projects push shipped 2026-07-30 (changelog 176) and he used it the same day:
//
//   *"the competition bumper plate config is a little weird how you've got to design a plate
//    or plates, go into the 'Add to a project' menu, then select colors from scratch again…
//    design a plate, go into the add to a project menu, add that plate, then close the window,
//    change the plate color in the editor, go back to the add to a project menu again, add it,
//    repeat."*
//
// The root cause is not the loop — it is that **the send dialog was doing colour work**. It
// offered a bare `<input type="color">` per row, divorced from the editor, which is where
// every real colour tool in this app lives: palette extraction from the logo, the eyedropper,
// the saved swatches, the cascade. So a rep building a genuine multi-colour set either fought
// a naked colour well or ran that close → recolour → reopen loop.
//
// His fix, and it is the right one:
//
//   *"perhaps in the projects drop-down menu there should be an 'Add to project' button which
//    primes whatever it is you just designed, then once you've primed everything you go in
//    more fully/comprehensively, see all the designs you quasi-saved/sent/favorited for a
//    project, and then send them all at once."*
//
// So: **the editor becomes the only place a plate is designed.** This module is where a
// primed plate waits. The send dialog (ui/project-push.js) becomes purely "where do these go".
//
// ── ONE ENVELOPE PER FINGERPRINT, NOT ONE PER ITEM ──────────────────────────────────
// This is the design decision the module is built around, and it is a storage one. A WD
// autosave envelope carries the buyer's logo as base64 at up to 4096px — routinely megabytes.
// Storing a full envelope per primed item would blow the ~5 MB `localStorage` quota after
// about THREE primes, which is fewer than the colour sweep that motivated the feature.
//
// So envelopes live once per fingerprint in `bases: { [fp]: envelopeString }`, and an ITEM is
// tiny: `{ id, fp, product, bodyColor, layoutPreset, weights, thumb, addedAt }`. Because
// `designFingerprint` normalises the plate colour OUT (see that module's header), a four-colour
// sweep of one design is ONE base and FOUR items. That is the whole reason the fingerprint
// excludes the keyed fields rather than hashing the lot.
//
// ⚠ A BASE MUST BE DROPPED WHEN ITS LAST ITEM GOES. Otherwise the tray leaks megabytes for
// the rest of the browser profile's life — a rep who primes and removes ten plates over a week
// would eventually hit quota with an EMPTY-looking tray, which is the worst possible bug
// because there is nothing on screen to delete. `dropOrphanBases` runs on every mutation.
//
// ── QUOTA IS STILL NOT GUARANTEED, AND A PRIME MUST NEVER FAIL ──────────────────────
// One 4096px logo can exceed the quota on its own. When the write throws, the tray falls back
// to IN-MEMORY for the session, clears the persisted copy (so a reload shows an honest empty
// tray rather than a stale half of one) and raises a flag the dialog reads and says out loud.
// The prime itself always succeeds. Losing a staged plate on refresh is a bad afternoon;
// a button that silently does nothing is a bug report.
//
// ── PURE REDUCERS, THEN A THIN STORAGE SHELL ────────────────────────────────────────
// Everything above the "storage" divider is a pure function of a plain `{ items, bases }`
// object — no DOM, no localStorage, no clock unless one is handed in. That is what lets
// test/ui-logic.smoke.mjs drive prime/remove/dedupe/base-dropping directly in node, which is
// where the base-leak assertion lives. Reducers RETURN a new tray; they never mutate the one
// passed in, so a caller can always compare before and after.
//
// ONE GLOBAL TRAY, not one per project — the project is chosen once, at send ("send them all
// at once"). Anything else would ask the rep to pick a project before they have decided what
// they are sending, which is the loop this feature exists to delete.
// ============================================================================

/** Matches `AUTOSAVE_KEY = 'wd:v2:autosave'` (ui/state.js:52) — same app, same namespace. */
export const TRAY_KEY = 'wd:v2:project-tray';

/** Bumped when the item shape changes. An older/newer payload is IGNORED, not migrated:
 *  a staged plate is a scratch note the rep can recreate in two clicks, and a half-understood
 *  migration of a megabyte base is a worse failure than an empty tray. */
export const TRAY_VERSION = 1;

/** A sanity ceiling. Nothing in the UI encourages 40 staged plates, but a stuck loop could
 *  write them, and each one is a render + an upload at send time. Oldest are dropped first. */
export const TRAY_MAX_ITEMS = 24;

/** Per item. The chips can't offer more than a product's whole ladder (the dumbbell's eleven). */
export const TRAY_MAX_WEIGHTS = 16;

// ---------------------------------------------------------------------------
// Pure reducers — no DOM, no storage. Everything below `emptyTray` is testable in node.
// ---------------------------------------------------------------------------

/** A brand-new, empty tray. */
export function emptyTray() {
  return { items: [], bases: {} };
}

/**
 * What makes two primes THE SAME staged plate.
 *
 * The fingerprint answers "same artwork?"; the other three are the dimensions the artwork
 * hash deliberately does NOT cover because the R2 key already does (see design-fingerprint.js).
 * Together they are exactly the identity of one plate picture — which is also exactly the
 * uniqueness the key needs, so a tray that de-dupes on this can never stage two items that
 * would fight over one R2 object.
 */
export function itemIdentity(item) {
  const it = item || {};
  return [it.fp || '', it.product || '', String(it.bodyColor || '').toUpperCase(), it.layoutPreset || ''].join('|');
}

/** Normalise a weights list: finite, positive, deduped, order preserved, capped. */
export function normaliseWeights(list) {
  if (!Array.isArray(list)) return [];
  const seen = new Set();
  const out = [];
  for (const raw of list) {
    const n = Number(raw);
    if (!Number.isFinite(n) || n <= 0) continue;
    if (seen.has(n)) continue;
    seen.add(n);
    out.push(n);
    if (out.length >= TRAY_MAX_WEIGHTS) break;
  }
  return out;
}

/** Ids only have to be unique within one tray, and they are never shown. */
let idSeq = 0;
export function newItemId() {
  idSeq += 1;
  return `pt_${Date.now().toString(36)}${idSeq.toString(36)}${Math.random().toString(36).slice(2, 6)}`;
}

/**
 * Drop every base no surviving item points at.
 *
 * The leak guard — see the header. Runs after EVERY mutation rather than only after a remove,
 * because a replace can also strand a base (re-prime the same slot with new art and the old
 * fingerprint's envelope has no referent left).
 */
function dropOrphanBases(tray) {
  const live = new Set(tray.items.map((it) => it.fp).filter(Boolean));
  const bases = {};
  for (const fp of Object.keys(tray.bases || {})) {
    if (live.has(fp)) bases[fp] = tray.bases[fp];
  }
  return { items: tray.items, bases };
}

/**
 * Stage one plate.
 *
 * @param {{items:Array, bases:Object}} tray
 * @param {object} entry
 * @param {string} entry.fp          the artwork fingerprint (design-fingerprint.js)
 * @param {string} entry.envelope    the autosave envelope — stored ONCE per `fp`
 * @param {string} entry.product     a products.js key
 * @param {string|null} entry.bodyColor  the plate dye, or null when undyed/not dyeable
 * @param {string|null} entry.layoutPreset  the DERIVED layout id, for the key and the label
 * @param {string|null} [entry.thumb]  a small 2D data-URL; patched in later, may be absent
 * @param {number[]} [entry.weights]   pre-chosen denominations — an EXPLICIT list that wins over what
 *                                     a re-primed row already had (tests, and a caller that means it)
 * @param {number[]} [entry.seedWeights] the rung this configuration was DESIGNED at (WD 460) — used
 *                                     only when the row is new or has no denominations yet, so a
 *                                     re-prime never overwrites chips the rep has already ticked
 * @param {string} [entry.id]          supplied only by tests, which need determinism
 * @param {number} [entry.addedAt]     ditto
 * @returns {{tray: object, item: object, replaced: boolean}}
 */
export function primeInto(tray, entry) {
  const base = tray && Array.isArray(tray.items) ? tray : emptyTray();
  const e = entry || {};
  const item = {
    id: e.id || newItemId(),
    fp: e.fp || '',
    product: e.product || '',
    bodyColor: e.bodyColor ? String(e.bodyColor).toUpperCase() : null,
    layoutPreset: e.layoutPreset || null,
    // Carried forward on a re-prime below, so a rep who ticked "45 and 25", then went back to
    // the editor to nudge the logo and primed again, does not lose the denominations.
    weights: normaliseWeights(e.weights),
    /* WHICH PICTURE THIS ROW MAKES — 'each' (one image per denomination, the historical and
       default behaviour) or 'fan' (the whole set composed into one, `packet-fan.js`).
       Ryan 2026-07-31: *"reps would have the option to choose which image they generate based on
       the customers needs… the rep would never select both on the same rendering — either/or."*
       So it is one value on the row, not a second row and not a checkbox pair.
       ⚠ NOT a prime-time decision — priming captures the ARTWORK, and this is a question about
       how to photograph it, asked in the review dialog beside the denominations for exactly the
       reason those live there too. It is carried across a re-prime below for the same reason
       weights are: nudging a logo and re-priming must not silently undo a rep's choice. */
    render: e.render === 'fan' ? 'fan' : 'each',
    thumb: e.thumb || null,
    addedAt: Number.isFinite(e.addedAt) ? e.addedAt : Date.now(),
  };

  const identity = itemIdentity(item);
  const at = base.items.findIndex((it) => itemIdentity(it) === identity);

  let items;
  let replaced = false;
  if (at >= 0) {
    // REPLACE IN PLACE, keeping the original id and slot. Ryan will re-prime the same plate
    // constantly — tweak the logo size, prime; nudge it, prime — and every one of those is the
    // same plate at a newer moment, not a fifth row. Keeping the slot means the list does not
    // reshuffle under the pointer while he works.
    replaced = true;
    const prev = base.items[at];
    items = base.items.slice();
    items[at] = {
      ...item,
      id: prev.id,
      // Explicit wins; then what the row already had; the seed only fills a row that has nothing.
      weights: item.weights.length ? item.weights : (prev.weights.length ? prev.weights : normaliseWeights(e.seedWeights)),
      // …and the render mode survives too — a prime never carries one, so an explicit choice made
      // in the review dialog would otherwise be reset by the next tweak-and-re-prime.
      render: e.render ? item.render : (prev.render || 'each'),
    };
  } else {
    if (!item.weights.length) item.weights = normaliseWeights(e.seedWeights);
    items = [...base.items, item];
    // Oldest out first, and it is the OLDEST that goes rather than the newest being refused:
    // a refusal is a button that did nothing, which is the failure mode this whole module is
    // written to avoid.
    if (items.length > TRAY_MAX_ITEMS) items = items.slice(items.length - TRAY_MAX_ITEMS);
  }

  const bases = { ...base.bases };
  // Only when we actually have one, and only when it is new: re-storing an identical
  // multi-megabyte string on every re-prime is pure churn against the quota.
  if (item.fp && typeof e.envelope === 'string' && e.envelope && !bases[item.fp]) {
    bases[item.fp] = e.envelope;
  }

  const next = dropOrphanBases({ items, bases });
  return { tray: next, item: items.find((it) => itemIdentity(it) === identity) || item, replaced };
}

/** Un-stage one plate by id. Drops its base if it was the last item using that artwork. */
export function removeItemFrom(tray, id) {
  const base = tray && Array.isArray(tray.items) ? tray : emptyTray();
  const items = base.items.filter((it) => it.id !== id);
  if (items.length === base.items.length) return base;
  return dropOrphanBases({ items, bases: { ...base.bases } });
}

/** Set one item's denominations (the send dialog's chips). */
export function setItemWeightsIn(tray, id, weights) {
  const base = tray && Array.isArray(tray.items) ? tray : emptyTray();
  const next = normaliseWeights(weights);
  let hit = false;
  const items = base.items.map((it) => {
    if (it.id !== id) return it;
    hit = true;
    return { ...it, weights: next };
  });
  return hit ? { items, bases: { ...base.bases } } : base;
}

/** The envelope for one item's artwork, or null. */
export function baseFor(tray, fp) {
  const bases = (tray && tray.bases) || {};
  return (fp && typeof bases[fp] === 'string') ? bases[fp] : null;
}

/** How many plates are staged. The count the Projects card shows. */
export function trayCount(tray) {
  return (tray && Array.isArray(tray.items)) ? tray.items.length : 0;
}

/**
 * Accept-or-reject a parsed payload. Anything malformed becomes an EMPTY tray, never a
 * partially-trusted one — see TRAY_VERSION.
 */
export function sanitiseTray(raw) {
  if (!raw || typeof raw !== 'object' || raw.v !== TRAY_VERSION) return emptyTray();
  const bases = {};
  if (raw.bases && typeof raw.bases === 'object') {
    for (const fp of Object.keys(raw.bases)) {
      if (typeof raw.bases[fp] === 'string') bases[fp] = raw.bases[fp];
    }
  }
  const items = Array.isArray(raw.items)
    ? raw.items
      .filter((it) => it && typeof it === 'object' && typeof it.id === 'string' && typeof it.fp === 'string')
      .map((it) => ({
        id: it.id,
        fp: it.fp,
        product: typeof it.product === 'string' ? it.product : '',
        bodyColor: it.bodyColor ? String(it.bodyColor).toUpperCase() : null,
        layoutPreset: typeof it.layoutPreset === 'string' ? it.layoutPreset : null,
        weights: normaliseWeights(it.weights),
        thumb: typeof it.thumb === 'string' ? it.thumb : null,
        addedAt: Number.isFinite(it.addedAt) ? it.addedAt : 0,
      }))
      // An item whose base didn't survive the trip is unrenderable — it has a picture and a
      // label and nothing to render. Dropping it here is kinder than letting it sit in the
      // list until Send reports one mystery failure.
      .filter((it) => typeof bases[it.fp] === 'string')
      .slice(0, TRAY_MAX_ITEMS)
    : [];
  return dropOrphanBases({ items, bases });
}

/** The wire form. Kept beside `sanitiseTray` so the two shapes can't drift. */
export function serialiseTray(tray) {
  const t = (tray && Array.isArray(tray.items)) ? tray : emptyTray();
  return JSON.stringify({ v: TRAY_VERSION, items: t.items, bases: t.bases });
}

// ---------------------------------------------------------------------------
// Storage shell — the ONE stateful thing in this module. Browser-only in practice;
// every function degrades to the in-memory tray when there is no localStorage at all.
// ---------------------------------------------------------------------------

let cache = null;          // the live tray, once read
let memoryOnly = false;    // true after a quota failure — see the header
const listeners = new Set();

function storage() {
  try {
    // The property access itself throws in some locked-down privacy modes, which is why this
    // is wrapped rather than merely null-checked.
    return (typeof localStorage !== 'undefined' && localStorage) ? localStorage : null;
  } catch { return null; }
}

function isQuotaError(err) {
  if (!err) return false;
  return err.name === 'QuotaExceededError'
    || err.name === 'NS_ERROR_DOM_QUOTA_REACHED'   // Firefox's own name for it
    || err.code === 22 || err.code === 1014;
}

/** The staged tray. Read once, then served from memory. */
export function getTray() {
  if (cache) return cache;
  const store = storage();
  if (!store) { memoryOnly = true; cache = emptyTray(); return cache; }
  let raw = null;
  try { raw = store.getItem(TRAY_KEY); } catch { raw = null; }
  let parsed = null;
  try { parsed = raw ? JSON.parse(raw) : null; } catch { parsed = null; }
  cache = sanitiseTray(parsed);
  return cache;
}

/**
 * Replace the tray and persist it.
 *
 * @returns {{ok: boolean, memoryOnly: boolean}} `ok` is about the WRITE, never about the
 *   staging — the tray in memory is updated either way, which is the promise the header makes.
 */
export function setTray(tray) {
  cache = (tray && Array.isArray(tray.items)) ? tray : emptyTray();
  const store = storage();
  if (store && !memoryOnly) {
    try {
      store.setItem(TRAY_KEY, serialiseTray(cache));
    } catch (err) {
      if (!isQuotaError(err)) { /* a non-quota write failure is still a failure to persist */ }
      memoryOnly = true;
      // Clear the stale copy. Two reasons: it frees whatever the last good write was holding
      // (which is what put us over in the first place), and it stops a reload from resurrecting
      // a tray that is a subset of what the rep can see on screen right now.
      try { store.removeItem(TRAY_KEY); } catch { /* nothing left to try */ }
    }
  }
  for (const fn of listeners) { try { fn(cache); } catch { /* a listener must never break a prime */ } }
  return { ok: !memoryOnly, memoryOnly };
}

/** True once a write has failed — the dialog says so out loud rather than lying about safety. */
export function isTrayMemoryOnly() {
  return memoryOnly;
}

/**
 * Subscribe to tray changes. Used by the Projects card so "Review & send (3)…" is a LIVE
 * count rather than whatever it was when the shell booted.
 * @returns {Function} unsubscribe
 */
export function subscribeTray(fn) {
  if (typeof fn !== 'function') return () => {};
  listeners.add(fn);
  return () => listeners.delete(fn);
}

// ── the three mutations the UI actually performs ───────────────────────────────────────

/** Stage the plate described by `entry`. Always succeeds; may fall back to memory-only. */
export function primeToTray(entry) {
  const { tray, item, replaced } = primeInto(getTray(), entry);
  const write = setTray(tray);
  return { item, replaced, count: trayCount(tray), ...write };
}

/** Patch a field onto a staged item — used to fill in the thumbnail once it has rendered,
 *  so the prime itself can return before any rasterising happens. A no-op if the item has
 *  since been removed, which is the honest outcome for a picture nobody is waiting for. */
export function patchTrayItem(id, patch) {
  const tray = getTray();
  let hit = false;
  const items = tray.items.map((it) => {
    if (it.id !== id) return it;
    hit = true;
    return { ...it, ...patch, id: it.id };
  });
  if (!hit) return { ok: true, memoryOnly };
  return setTray({ items, bases: { ...tray.bases } });
}

export function removeTrayItem(id) {
  return setTray(removeItemFrom(getTray(), id));
}

export function setTrayItemWeights(id, weights) {
  return setTray(setItemWeightsIn(getTray(), id, weights));
}

/** Empty it — after a successful send, or from the dialog's own "Clear". A clear always
 *  fits, so it is also the one path that can bring a memory-only session back to persisted. */
export function clearTray() {
  memoryOnly = false;
  return setTray(emptyTray());
}

/** Test seam: forget everything this module is holding, including the quota flag. Not used
 *  by the app — a browser session is meant to keep its tray. */
export function resetTrayForTests() {
  cache = null;
  memoryOnly = false;
  listeners.clear();
}

/* ── REMEMBERED ARRANGEMENT, PER PROJECT (Ryan 2026-07-31) ────────────────────────────────────
   *"if I send weights to a specific project using a specific 'PUT THEM' type, it should remember
   that on each future push using this WD design."*

   Keyed by PROJECT, not by design. A project is a customer's proposal and has one house layout —
   the Lakesong packet wants its weights arranged the same way whether the plates came from one
   design or four. Keying by design would mean two designs sent to the same project disagreeing
   about how that project's pages are built, which is the opposite of the thing being asked for.

   Kept OUT of the tray blob deliberately: the tray is cleared, emptied and quota-evicted, and a
   preference must not evaporate with it. Separate key, tiny, and its own failure is silent —
   a forgotten dropdown is not worth a message.

   Not on the project record either. The worker already persists `packetSectionOrder` and
   `brandTheme` there, so it would be the tidier home — but this is a per-rep habit about how they
   like to build a packet, it is read only by this dialog, and putting it server-side would buy a
   schema change and a round trip for a select's default value. */
export const ARRANGE_KEY = 'wd:v2:project-arrange';

function readArrangeMap() {
  const store = storage();
  if (!store) return {};
  try {
    const parsed = JSON.parse(store.getItem(ARRANGE_KEY) || '{}');
    return (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) ? parsed : {};
  } catch { return {}; }
}

/** The arrangement last used for `projectId`, or '' if there isn't one. */
export function rememberedArrangement(projectId) {
  if (!projectId) return '';
  const v = readArrangeMap()[String(projectId)];
  return typeof v === 'string' ? v : '';
}

/** Record the arrangement a send actually used. Silent on failure — see the header. */
export function rememberArrangement(projectId, arrangement) {
  if (!projectId || !arrangement) return;
  const store = storage();
  if (!store) return;
  const map = readArrangeMap();
  if (map[String(projectId)] === arrangement) return;
  map[String(projectId)] = String(arrangement);
  // Bounded: a rep with a hundred projects should not carry a hundred entries forever. Oldest
  // insertions drop first, which for an object literal is simply key order.
  const keys = Object.keys(map);
  if (keys.length > 50) for (const k of keys.slice(0, keys.length - 50)) delete map[k];
  try { store.setItem(ARRANGE_KEY, JSON.stringify(map)); } catch { /* a lost preference is not an error */ }
}

/* ── THE PROJECT A REP LAST SENT TO ──────────────────────────────────────────────────────────
   Ryan 2026-07-31: *"if at any point someone pushes products from the WD to a project, it should
   remember their selection in the PROJECT drop-down, so the person can quickly and easily push to
   the same project… requiring a manual override if they want to push to another project, which is
   almost never the case unless the customer comes back to us to buy again later down the road, in
   which case, once we push to that new project, it'll remember that one going forward."*

   ONE id, not a map — unlike the arrangement, which is a fact ABOUT a project and therefore keyed
   by one. This is a fact about the REP: where their work is going right now. A rep builds a
   proposal over days and sends to it a dozen times, so "the last one" is the right answer nearly
   every time, and the one time it isn't they change it and that becomes the new answer.

   ⚠ Written only on a SUCCESSFUL send. Remembering a project that was picked and then failed (or
   was cancelled) would quietly aim the next push at somewhere nothing has ever landed.

   ⚠ And it is only ever a DEFAULT. The dialog re-selects it if the project is still in the rep's
   list and otherwise leaves the picker on "Choose a project…" — a remembered id that has been
   deleted must never become a silent selection, because the one thing worse than picking the
   project every time is sending a customer's plates to a project the rep didn't look at. */
export const LAST_PROJECT_KEY = 'wd:v2:project-last';

/** The project this rep last successfully sent to, or '' if there isn't one. */
export function rememberedProject() {
  const store = storage();
  if (!store) return '';
  try {
    const v = store.getItem(LAST_PROJECT_KEY);
    return typeof v === 'string' ? v : '';
  } catch { return ''; }
}

/** Record the project a send actually landed in. Silent on failure — see the header. */
export function rememberProject(projectId) {
  if (!projectId) return;
  const store = storage();
  if (!store) return;
  try { store.setItem(LAST_PROJECT_KEY, String(projectId)); } catch { /* a lost preference is not an error */ }
}
